/**
 * Central brand identity. Reads VITE_BRAND_* env (see .env.example), inlined
 * into the bundle at build time — so one codebase builds multiple white-label
 * apps by changing env only (mirrors the BE's `branding:` config block).
 *
 * Defaults below are xFinCompass — this repo is xFinCompass, so an unconfigured
 * build is an xFinCompass build.
 *
 * SCOPE: user-facing copy only — legal pages, auth, nav, `<head>`/OG, AI
 * disclaimers. Deliberately NOT used for localStorage keys, the `xfincompass://`
 * deep-link scheme, IndexedDB names or CSS keyframe names: those are internal
 * identifiers, invisible to users, and renaming them would silently invalidate
 * state already persisted in existing users' browsers.
 *
 * For localized copy, do NOT import this module — locale JSON uses `{{brand}}`,
 * which resolves via i18next `interpolation.defaultVariables` (see i18n.ts).
 */
import { XFINCOMPASS_PRESET, TOPONELOGIC_PRESET } from "@/shared/config/brand-presets";

/**
 * Active brand preset, chosen by the single env var VITE_BRAND
 * (xfincompass | toponelogic). Selected STATICALLY on the build-inlined value so
 * Rollup folds the ternary and drops the OTHER brand's strings from the shipped
 * bundle — a white-label build must not ship a rival brand's name (the deploy
 * guard greps dist/ for it). Unset/unknown → xFinCompass (default brand of this
 * repo); only an explicit VITE_BRAND=toponelogic selects the other preset. Each
 * VITE_BRAND_* below still overrides one field on top of the chosen preset.
 *
 * ⚠️ SYNC: this ternary is CỐ Ý lệch upstream `toponelogic_client` (nó đảo chiều).
 * Merge từ upstream về thì giữ phía shine_client — xem docs/SYNC-UPSTREAM.md.
 */
const preset =
  import.meta.env.VITE_BRAND === "toponelogic" ? TOPONELOGIC_PRESET : XFINCOMPASS_PRESET;

export const BRAND = {
  /** Display name, e.g. in nav, titles, disclaimers. */
  name: import.meta.env.VITE_BRAND_NAME || preset.name,

  /** Legal entity name used in terms/privacy/disclaimer copy. */
  legalName: import.meta.env.VITE_BRAND_LEGAL_NAME || preset.legalName,

  /** Short monogram rendered in the logo tile (nav rail, auth pages). */
  monogram: import.meta.env.VITE_BRAND_MONOGRAM || preset.monogram,

  /** Lowercase slug for user-visible filenames (e.g. CSV exports). */
  slug: import.meta.env.VITE_BRAND_SLUG || preset.slug,

  /** Out-of-box skin + accent for a brand-new browser. Consumed by
   * `entities/user/model/preferences-store` (DEFAULT_PREFERENCES + the
   * house-default migration), which owns the PaletteKey/AccentKey unions and
   * casts these strings into them. */
  defaultPalette: preset.defaultPalette,
  defaultAccent: preset.defaultAccent,

  /** Whether this build offers "connect a brokerage". False withholds the only
   * way in (the Portfolio overview button); the broker code itself still ships. */
  offersBrokerConnect: preset.offersBrokerConnect,

  /** Canonical apex domain — marketing site / "back to site" links. */
  domain: import.meta.env.VITE_BRAND_DOMAIN || preset.domain,

  /**
   * Absolute origin used for SEO canonical + OG URLs on standalone pages.
   * NOTE: the xFinCompass default is the .org apex, which is what the previous
   * hardcoded `<link rel="canonical">` tags pointed at (kept as-is to avoid a
   * silent SEO change — see DEPLOY-TOPONELOGIC.md).
   */
  siteUrl: import.meta.env.VITE_BRAND_SITE_URL || preset.siteUrl,

  /** Support contact shown on legal + help surfaces. */
  supportEmail: import.meta.env.VITE_BRAND_SUPPORT_EMAIL || preset.supportEmail,

  /** Legal contact (terms, disclaimer, arbitration notices). */
  legalEmail: import.meta.env.VITE_BRAND_LEGAL_EMAIL || preset.legalEmail,

  /** Privacy contact (privacy policy, data-subject requests). */
  privacyEmail: import.meta.env.VITE_BRAND_PRIVACY_EMAIL || preset.privacyEmail,
} as const;

/**
 * xFinCompass ships as a US-only product (single `stock` domain, US defaults,
 * US-gated copy). Canonical home of the flag — `@/shared/stores/app-store` re-exports
 * it for the many UI callers that already import it from there; pure modules
 * (calendar helpers, formatters) import from here to stay React-free.
 */
export const BRAND_US_ONLY = BRAND.slug === "xfincompass";

/**
 * Resolve `{{brand}}` tokens throughout a freshly loaded locale bundle.
 *
 * Substitution happens at LOAD time rather than through i18next's
 * `interpolation.defaultVariables` because i18next does not interpolate
 * strings nested inside `t(key, { returnObjects: true })` results — and much
 * of the UI reads list/object copy that way. Rewriting the bundle up front
 * makes every read path (plain `t()`, `returnObjects`, raw JSON) consistent.
 */
export function applyBrandTokens<T>(value: T): T {
  if (typeof value === "string") {
    return value.replace(/\{\{brand\}\}/g, BRAND.name) as unknown as T;
  }
  if (Array.isArray(value)) {
    return value.map(applyBrandTokens) as unknown as T;
  }
  if (value && typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      out[k] = applyBrandTokens(v);
    }
    return out as unknown as T;
  }
  return value;
}
